import * as Sentry from "@sentry/react";

import {
  useCoveragePriceCalculations,
  useFormState,
} from "Store/priceCalculation/priceCalculationHooks";
import {
  PREGNANT_AND_NEWBORN_ABOUT_CHILD_STEP,
  PREGNANT_AND_NEWBORN_PRICE_STEP,
} from "..";

import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { DittoCoverageCard } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/DittoCoverages/DittoCoverageCard";
import { getSelectedCoverage } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.helpers";
import { StyledComparisonWrap } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/productPriceTile.styles";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { getCollectionOfDittoComponents } from "Components/TextContext";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { TotalPriceMessage } from "Components/TotalPriceMessage";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products/index";
import { ReduxState } from "Store/rootReducer";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { Form } from "formik";
import React from "react";
import { useSelector } from "react-redux";
import { PregnantAndNewbornDetailsFormValues } from "../ProductDetails/pregnantAndNewbornProductDetailsInterfaces";
import { PregnantAndNewbornPriceFormValues } from "./pregnantAndNewbornProductPriceInterfaces";
import { coverageTypeFrom } from "./pregnantAndNewbornProductPriceUtils";
export const PregnantAndNewbornProductPrice = (): React.ReactElement => {
  const aboutStep = useFormState(
    PREGNANT_AND_NEWBORN_ABOUT_CHILD_STEP.id,
  ) as PregnantAndNewbornDetailsFormValues;
  const { addProductToCart } = useSelector((state: ReduxState) => state);
  const handleAddProductToCart = useAddProductToCartHandler(
    PREGNANT_AND_NEWBORN_PRICE_STEP.id,
  );
  const coverages = useCoveragePriceCalculations();
  const coverageType = coverageTypeFrom(
    aboutStep.childBorn,
    aboutStep.multipleChildren,
  );
  const selectedCoverage = getSelectedCoverage(coverageType, coverages);
  const price = selectedCoverage?.[1].prices[0].yearlyPricePrecise ?? 0;
  const initialFormState = useFormState(PREGNANT_AND_NEWBORN_PRICE_STEP.id, {
    coverage: coverageType,
  });
  const { pregnantandnewborn: productCardTextComponents } =
    getCollectionOfDittoComponents(TextComponentNameLevel1.COVERAGE_CARD);

  const [
    {
      formText: { checkPrice },
    },
    lang,
  ] = useProductTexts(
    getFMSProductName(INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN),
  );

  const handleSubmit = (formProps: PregnantAndNewbornPriceFormValues): any => {
    const coverageToAdd = {
      coverage: coverageType,
      productType: INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN,
    };
    handleAddProductToCart(formProps, [coverageToAdd]);
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductPrice - ${INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN}`,
        );
      }}
    >
      <ProductPriceTile
        validationSchema={""}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
        header={PREGNANT_AND_NEWBORN_PRICE_STEP.title}
      >
        <Form>
          {selectedCoverage && (
            <DittoCoverageCard
              fieldName="coverage"
              product={ProductBackendName.PREGNANT_AND_NEWBORN}
              coverage={selectedCoverage[0]}
              textComponents={productCardTextComponents}
              prices={selectedCoverage[1].prices}
              shortTermProduct
              hideRadioButton
              showExcludedCoverages={false}
            />
          )}
          <TotalPriceMessage
            message={checkPrice.totalPriceMessage[lang]}
            totalPriceValue={price}
          />
          {addProductToCart.error && (
            <SystemErrorAlert alertId="addProductToCart" marginTop="32px" />
          )}
          <StyledComparisonWrap>
            <NavigationButtons isLoading={addProductToCart.isLoading} />
          </StyledComparisonWrap>
          <InputFailureTracker
            productName={INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN}
            stepId={PREGNANT_AND_NEWBORN_PRICE_STEP.id}
          />
        </Form>
      </ProductPriceTile>
    </Sentry.ErrorBoundary>
  );
};
